/**
 * Garden footprints frogs must never enter — per yard, with safety padding.
 *
 * Centers match `garden-bed` props in the backyard prop packs (desktop layout).
 */

import type { SpawnPoint } from '@/world-prototype/types';

export interface FrogExclusionBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/** Shared raised-bed footprint offsets (world units from prop center). */
const GARDEN_BED_FOOTPRINT: readonly SpawnPoint[] = [
  { x: -0.038, y: 0.007 },
  { x: 0.038, y: 0.007 },
  { x: 0.034, y: -0.018 },
  { x: -0.034, y: -0.018 },
];

/** Extra margin so frogs never clip the bed edge. */
const GARDEN_EXCLUSION_PADDING = 0.016;

const HUBERT_GARDEN_CENTER: SpawnPoint = { x: 0.27468, y: 0.68541 };
const RALEIGH_GARDEN_CENTER: SpawnPoint = { x: 0.20392, y: 0.67621 };

function boundsFromGardenCenter(center: SpawnPoint): FrogExclusionBounds {
  const xs = GARDEN_BED_FOOTPRINT.map((p) => center.x + p.x);
  const ys = GARDEN_BED_FOOTPRINT.map((p) => center.y + p.y);
  return {
    minX: Math.min(...xs) - GARDEN_EXCLUSION_PADDING,
    maxX: Math.max(...xs) + GARDEN_EXCLUSION_PADDING,
    minY: Math.min(...ys) - GARDEN_EXCLUSION_PADDING,
    maxY: Math.max(...ys) + GARDEN_EXCLUSION_PADDING,
  };
}

const ZONES: Readonly<Record<string, readonly FrogExclusionBounds[]>> = {
  hubert: [boundsFromGardenCenter(HUBERT_GARDEN_CENTER)],
  raleigh: [boundsFromGardenCenter(RALEIGH_GARDEN_CENTER)],
};

export function frogExclusionZonesForYard(
  yardId: string | null | undefined,
): readonly FrogExclusionBounds[] {
  if (!yardId) return [];
  return ZONES[yardId] ?? [];
}

export function pointInFrogExclusionZone(
  point: SpawnPoint,
  zones: readonly FrogExclusionBounds[],
): boolean {
  for (const zone of zones) {
    if (
      point.x >= zone.minX &&
      point.x <= zone.maxX &&
      point.y >= zone.minY &&
      point.y <= zone.maxY
    ) {
      return true;
    }
  }
  return false;
}
